import Quagga from '@ericblade/quagga2';

const REQUIRED_SCANS = 2;

/**
 * Opens a fullscreen barcode scanner modal using Quagga2.
 * Works on iOS Safari + Android Chrome.
 * 
 * @param {Object} options
 * @param {string} options.modalId - Unique modal element ID
 * @param {string} options.scannerId - Unique scanner container ID
 * @param {function} options.onDetected - Called with the verified barcode string
 * @param {function} [options.onError] - Called if camera fails
 * @param {function} [options.onManualEntry] - Called when user clicks manual entry (receives cleanup fn)
 * @returns {{ cleanup: function }} - Call cleanup() to stop scanner
 */
export function openBarcodeScanner({ modalId, scannerId, onDetected, onError, onManualEntry }) {
  let scanVerification = { code: null, count: 0 };
  let stopped = false;

  const modalHtml = `
    <div id="${modalId}" class="fixed inset-0 z-[10000] bg-black bg-opacity-90 flex items-center justify-center p-4" style="z-index:10000">
      <div class="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl">
        <div class="p-4 border-b">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900">Scanner le code-barres</h3>
            <button id="${modalId}-close" class="text-gray-400 hover:text-gray-600 p-1">
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>
        <div class="p-4">
          <div id="${scannerId}" class="relative bg-black rounded-lg overflow-hidden" style="width:100%; min-height:250px;"></div>
          <div class="mt-3">
            <div id="${modalId}-status" class="text-center text-sm text-gray-600 py-2">
              Pointez la caméra vers le code-barres
            </div>
            <div id="${modalId}-verify" class="text-center text-xs text-yellow-600 py-1 hidden"></div>
          </div>
        </div>
        <div class="p-4 border-t flex flex-col gap-2">
          <button id="${modalId}-manual" class="w-full px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium">
            ✏️ Saisie manuelle du code
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  const modal = document.getElementById(modalId);
  const closeBtn = document.getElementById(`${modalId}-close`);
  const manualBtn = document.getElementById(`${modalId}-manual`);
  const statusEl = document.getElementById(`${modalId}-status`);
  const verifyEl = document.getElementById(`${modalId}-verify`);

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    try {
      Quagga.stop();
    } catch (e) {
      // ignore
    }
    if (modal && modal.parentNode) {
      modal.remove();
    }
  };

  closeBtn.addEventListener('click', cleanup);

  manualBtn.addEventListener('click', () => {
    cleanup();
    if (onManualEntry) {
      onManualEntry();
    } else {
      const userInput = prompt('Saisissez le code-barres manuellement (max 8 chiffres):');
      if (userInput && userInput.trim()) {
        const trimmed = userInput.trim();
        if (trimmed.length > 8) {
          alert('❌ Code invalide! Maximum 8 chiffres.');
          return;
        }
        onDetected(trimmed);
      }
    }
  });

  // Initialize Quagga2
  Quagga.init({
    inputStream: {
      name: 'Live',
      type: 'LiveStream',
      target: document.getElementById(scannerId),
      constraints: {
        facingMode: 'environment',
        width: { min: 640, ideal: 1280 },
        height: { min: 480, ideal: 720 },
      },
    },
    locator: {
      patchSize: 'medium',
      halfSample: true,
    },
    numOfWorkers: 0, // Use main thread (more reliable on iOS)
    decoder: {
      readers: [
        'code_128_reader',
        'ean_reader',
        'ean_8_reader',
        'code_39_reader',
        'code_93_reader',
        'upc_reader',
        'upc_e_reader',
        'codabar_reader',
        'i2of5_reader',
      ],
    },
    locate: true,
    frequency: 5,
  }, (err) => {
    if (err) {
      console.error('Quagga init error:', err);
      cleanup();
      if (onError) {
        onError(err);
      } else {
        const userInput = prompt('Caméra non disponible. Saisissez le code-barres manuellement:');
        if (userInput && userInput.trim() && userInput.trim().length <= 8) {
          onDetected(userInput.trim());
        }
      }
      return;
    }

    Quagga.start();

    // Style the video element for iOS
    const videoEl = document.querySelector(`#${scannerId} video`);
    if (videoEl) {
      videoEl.setAttribute('playsinline', 'true');
      videoEl.setAttribute('webkit-playsinline', 'true');
      videoEl.style.width = '100%';
      videoEl.style.height = 'auto';
      videoEl.style.objectFit = 'cover';
    }
    // Hide the canvas overlay (Quagga draws detection boxes)
    const canvasEls = document.querySelectorAll(`#${scannerId} canvas`);
    canvasEls.forEach(c => { c.style.display = 'none'; });
  });

  Quagga.onDetected((result) => {
    if (stopped) return;
    const code = result.codeResult.code;
    if (!code) return;

    // Confidence check — only accept if >50% of the code characters agree
    const errors = result.codeResult.decodedCodes;
    if (errors && errors.length > 0) {
      const avgError = errors
        .filter(d => d.error !== undefined)
        .reduce((sum, d) => sum + d.error, 0) / errors.filter(d => d.error !== undefined).length;
      if (avgError > 0.25) return; // Too noisy, skip
    }

    // Re-verification: same code must be detected REQUIRED_SCANS times
    if (scanVerification.code === code) {
      scanVerification.count++;
    } else {
      scanVerification = { code, count: 1 };
    }

    if (verifyEl) {
      verifyEl.classList.remove('hidden');
      verifyEl.textContent = `Vérification: ${scanVerification.count}/${REQUIRED_SCANS}`;
      verifyEl.className = scanVerification.count >= REQUIRED_SCANS
        ? 'text-center text-xs py-1 text-green-600 font-medium'
        : 'text-center text-xs py-1 text-blue-600 font-medium';
    }

    if (scanVerification.count < REQUIRED_SCANS) {
      if (statusEl) {
        statusEl.textContent = `Code détecté: ${code} — Gardez la caméra stable...`;
        statusEl.className = 'text-center text-sm text-orange-600 py-2';
      }
      return;
    }

    // Verified! Validate length
    if (code.length > 8) {
      if (statusEl) {
        statusEl.textContent = '❌ Code invalide! Max 8 chiffres. Réessayez...';
        statusEl.className = 'text-center text-sm text-red-600 py-2 font-medium';
      }
      scanVerification = { code: null, count: 0 };
      return;
    }

    // Success
    cleanup();
    onDetected(code);
  });

  return { cleanup };
}
